/**
 * timeBlocksAdmin.service.ts — Admin > Appointments > Time Blocks (legacy
 * admin_time_block_list.php): every block on the account's calendar, one row
 * per block (a recurring block is one row), from GET /appointment-blocks.
 * Editing and deleting go through the scheduler's own block calls.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminTimeBlock {
  id: number;
  title: string;
  /** UTC ISO. */
  start: string | null;
  end: string | null;
  resourceIds: number[];
  facilityId: number | null;
  appointmentTypeId: number | null;
  /** blockType 1: open for booking; 2: not available. */
  available: boolean;
  required: boolean;
  /** Legacy's recurrence row: active = recurring. */
  recurring: boolean;
  /** 1 indefinitely, 2 for N days, 3 until a date. */
  timeframeType: number;
  timeframeDuration: number;
  timeframeDate: string | null;
  days: string[];
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function idOrNull(v: unknown): number | null {
  const n = num(v);
  return n > 0 ? n : null;
}

export function toAdminTimeBlock(raw: Record<string, unknown>): AdminTimeBlock {
  const rec = (raw.recurrence ?? {}) as Record<string, unknown>;
  const timeframeDate = typeof rec.timeframeDate === 'string' && rec.timeframeDate && !rec.timeframeDate.startsWith('0000') ? rec.timeframeDate.slice(0, 10) : null;
  return {
    id: num(raw.blockTimeId),
    title: typeof raw.title === 'string' ? raw.title : '',
    start: typeof raw.startTime === 'string' ? raw.startTime : null,
    end: typeof raw.endTime === 'string' ? raw.endTime : null,
    resourceIds: Array.isArray(raw.resourceIds) ? raw.resourceIds.map(num).filter((n) => n > 0) : [],
    facilityId: idOrNull(raw.facilityId),
    appointmentTypeId: idOrNull(raw.appointmentTypeId),
    available: num(raw.blockAvailability ?? rec.blockType) === 1,
    required: Boolean(raw.required ?? rec.required),
    recurring: num(rec.active) === 1,
    timeframeType: num(rec.timeframeType) || 1,
    timeframeDuration: num(rec.timeframeDuration),
    timeframeDate,
    days: DAY_KEYS.filter((d) => num(rec[d]) === 1),
  };
}

export function listTimeBlocks(): Promise<AdminTimeBlock[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/appointment-blocks')
    .then((r) => (r.data.data ?? []).map(toAdminTimeBlock).filter((b) => b.id > 0));
}

/** Legacy's Occurrence text: "Once", or "Recurring until 2026-12-31 every Mon, Wed". */
export function occurrenceText(b: Pick<AdminTimeBlock, 'recurring' | 'timeframeType' | 'timeframeDuration' | 'timeframeDate' | 'days'>): string {
  if (!b.recurring) return 'Once';
  const span =
    b.timeframeType === 2 ? `for ${b.timeframeDuration} day(s)` : b.timeframeType === 3 && b.timeframeDate ? `until ${b.timeframeDate}` : 'indefinitely';
  const days = b.days.map((d) => DAY_LABELS[d as (typeof DAY_KEYS)[number]] ?? d).join(', ');
  return days ? `Recurring ${span} every ${days}` : `Recurring ${span}`;
}
